import { trackTelemetry } from './telemetry';

const DB_NAME = 'hobtalk-apollo-cache';
const STORE_NAME = 'cache';
const DB_VERSION = 1;
const SAVE_DEBOUNCE_MS = 800;

let saveTimer = null;

/**
 * Determines whether indexed dbavailable.
 *
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isIndexedDBAvailable() {
  return typeof window !== 'undefined' && Boolean(window.indexedDB);
}

/**
 * Performs the safe key operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function safeKey(value) {
  return String(value || 'anonymous').replace(/[^a-zA-Z0-9_-]/g, '_');
}

/**
 * Performs database.
 *
 * @returns {*} The derived operation result.
 */
function openDatabase() {
  if (!isIndexedDBAvailable()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Runs one IndexedDB request and closes its transaction-owned connection. */
function runStore(mode, action) {
  return openDatabase().then((db) => {
    if (!db) return undefined;
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      const request = action(store);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  });
}

/**
 * Returns the stable, account-scoped key used to isolate persisted Apollo data.
 * MSAL's home account ID is preferred because usernames and display claims can change.
 *
 * @param {object} account - MSAL account whose cache namespace is required.
 * @returns {string} Filesystem-safe IndexedDB record key.
 */
export function apolloCacheKeyForAccount(account) {
  const claims = account?.idTokenClaims || {};
  return safeKey(account?.homeAccountId || claims.oid || account?.localAccountId || account?.username);
}

/**
 * Restores a persisted normalized cache before initial queries render.
 * Failures are intentionally non-fatal because the network remains the source of truth.
 *
 * @param {import('@apollo/client').ApolloCache<object>} cache - Apollo cache to hydrate.
 * @param {string} accountKey - Stable account-scoped persistence key.
 * @returns {Promise<boolean>} Whether a usable snapshot was restored.
 */
export async function restoreApolloCache(cache, accountKey) {
  try {
    const snapshot = await runStore('readonly', (store) => store.get(safeKey(accountKey)));
    if (snapshot && typeof snapshot === 'object') {
      cache.restore(snapshot);
      trackTelemetry('cache.hit', { group: 'apollo' }, { category: 'cache' });
      return true;
    }
    trackTelemetry('cache.miss', { group: 'apollo' }, { category: 'cache' });
  } catch {
    trackTelemetry('cache.restore.failed', { group: 'apollo' }, { severity: 'warning', category: 'cache' });
    // Cache restore is best-effort. Network remains the source of truth.
  }
  return false;
}

/**
 * Debounces an Apollo snapshot write so query bursts do not create IndexedDB churn.
 *
 * @param {import('@apollo/client').ApolloCache<object>} cache - Apollo cache to persist.
 * @param {string} accountKey - Stable account-scoped persistence key.
 * @returns {void} The write is scheduled asynchronously.
 */
export function scheduleApolloCachePersist(cache, accountKey) {
  if (!accountKey) return;
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    persistApolloCache(cache, accountKey);
  }, SAVE_DEBOUNCE_MS);
}

/**
 * Persists the current normalized Apollo cache for one signed-in account.
 *
 * @param {import('@apollo/client').ApolloCache<object>} cache - Apollo cache to serialize.
 * @param {string} accountKey - Stable account-scoped persistence key.
 * @returns {Promise<void>} Resolves after the best-effort write finishes.
 */
export async function persistApolloCache(cache, accountKey) {
  try {
    const snapshot = cache.extract();
    await runStore('readwrite', (store) => store.put(snapshot, safeKey(accountKey)));
  } catch {
    // Persisting cache should never block app usage.
  }
}

/**
 * Clears one account's persisted cache, or every account when no key is supplied.
 *
 * @param {string} [accountKey] - Optional account-scoped persistence key.
 * @returns {Promise<void>} Resolves after best-effort cleanup.
 */
export async function clearPersistedApolloCache(accountKey) {
  window.clearTimeout(saveTimer);
  saveTimer = null;
  try {
    if (accountKey) {
      await runStore('readwrite', (store) => store.delete(safeKey(accountKey)));
    } else {
      await runStore('readwrite', (store) => store.clear());
    }
  } catch {
    // Logout should continue even if browser cache cleanup fails.
  }
}
